/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { darken } from "polished";
import { Search as SearchIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  InputBase,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";

import { MiraTheme } from "../../theme/theme";
import { DataProject } from "../../../../api/dataprojects";
import { Contact, searchForContacts } from "../../../../api/contacts";
import {
  AccountSearchResult,
  searchForAccounts,
} from "../../../../api/accounts";
import { SuggestedChangeDetailsModal } from "../../../suggested-changes/RecordDetails";
import { RecordReviewModal } from "../../../record-review/RecordReviewModal";
import { AccountSummaryModal } from "../../../accounts/AccountSummaryModal";
import { displayCombinedRecordModal } from "../../../../utils/uiPreference";

const SearchContainer = styled.div<{ theme?: MiraTheme }>`
  position: relative;
  display: none;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`;

const SearchBox = styled.div<{ theme?: MiraTheme }>`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)};
  padding-right: ${(props) => props.theme.spacing(2)};
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }
`;

const SearchResultsContainer = styled(Paper)<{ theme?: MiraTheme }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: ${(props) => props.theme.spacing(1)};
  max-height: 400px;
  overflow-y: auto;
  z-index: 1300;
`;

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
  min-width: 350px;
`;

const Input = styled(InputBase)<{ theme?: MiraTheme }>`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
  }
`;

const RecentItemsContainer = styled.div<{ theme?: MiraTheme }>`
  display: none;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)};
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  ${(props) => props.theme.breakpoints.up("lg")} {
    display: flex;
    justify-content: flex-end;
  }
`;

type SearchResult = {
  id: number;
  name: string;
  type: "data-project" | "contact" | "account";
  contact?: Contact;
  account?: AccountSearchResult;
  /*
   * Accounts only. Contacts open a modal that takes the live selectedSystemId
   * as a prop, but an account's system is baked into the URL, so a recent chip
   * has to carry the system it was found in or it points at the wrong one once
   * the user switches system.
   */
  systemId?: number;
};

type RecentItem = {
  id: number;
  name: string;
  type: "data-project" | "contact" | "account";
  contact?: Contact;
  account?: AccountSearchResult;
  systemId?: number;
  timestamp: number;
};

const RECENT_ITEMS_KEY = "navbar-search-recent-items";

type NavbarSearchProps = {
  dataProjects?: DataProject[];
  selectedSystemId?: number;
};

export const NavbarSearch: React.FC<NavbarSearchProps> = ({
  dataProjects,
  selectedSystemId,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [accounts, setAccounts] = useState<AccountSearchResult[]>([]);
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
  const [isContactsModalOpen, setIsContactsModalOpen] =
    useState<boolean>(false);
  const [selectedContactId, setSelectedContactId] = useState<number>();
  /*
   * Captured when the contact is opened rather than read on every render, so
   * toggling the preference from the navbar cannot swap the surface out from
   * under a steward who is mid-decision - which matters more here than
   * anywhere else, since the toggle sits in the same navbar as this search.
   */
  const [useCombinedModal, setUseCombinedModal] = useState<boolean>(false);
  /*
   * Accounts keep their own selection and flag: the account modal is a
   * different surface from either contact one, and the preference-off path
   * for an account is still a page navigation rather than a second modal.
   */
  const [selectedAccount, setSelectedAccount] = useState<{
    systemId: number;
    crmAccountId: string;
  }>();
  const [isAccountModalOpen, setIsAccountModalOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLLIElement>(null);

  // Load recent items from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_ITEMS_KEY);
      if (stored) {
        const items = JSON.parse(stored) as RecentItem[];
        setRecentItems(items);
      }
    } catch (error) {
      console.error("Error loading recent items:", error);
    }
  }, []);

  // Save a recent item to localStorage
  const addRecentItem = (result: SearchResult) => {
    const newItem: RecentItem = {
      ...result,
      timestamp: Date.now(),
    };

    setRecentItems((prev) => {
      // Remove duplicates and add new item at the beginning
      const filtered = prev.filter(
        (item) => !(item.id === result.id && item.type === result.type)
      );
      const updated = [newItem, ...filtered];

      // Save to localStorage
      try {
        localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error("Error saving recent items:", error);
      }

      return updated;
    });
  };

  const removeRecentItem = (id: number, type: RecentItem["type"]) => {
    setRecentItems((prev) => {
      const updated = prev.filter(
        (item) => !(item.id === id && item.type === type)
      );
      try {
        localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error("Error saving recent items:", error);
      }
      return updated;
    });
  };

  // Get recent data projects (max 3)
  const recentProjects = React.useMemo(() => {
    return recentItems
      .filter((item) => item.type === "data-project")
      .slice(0, 3);
  }, [recentItems]);

  // Get recent contacts (max 3)
  const recentContacts = React.useMemo(() => {
    return recentItems.filter((item) => item.type === "contact").slice(0, 3);
  }, [recentItems]);

  // Get recent accounts (max 3)
  const recentAccounts = React.useMemo(() => {
    return recentItems.filter((item) => item.type === "account").slice(0, 3);
  }, [recentItems]);

  const filteredProjects = React.useMemo(() => {
    if (!dataProjects || !searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    return dataProjects.filter((project) =>
      project.name?.toLowerCase().includes(query)
    );
  }, [dataProjects, searchQuery]);

  // Search for contacts when search query changes
  useEffect(() => {
    /*
     * Set before the debounce rather than inside the request, because the
     * 300ms wait is part of the same gap: until the response lands, the list
     * still holds the previous query's contacts, and saying nothing would
     * present them as results for what has now been typed.
     */
    const pending = Boolean(searchQuery.trim() && selectedSystemId);
    setIsSearchingContacts(pending);
    setIsSearchingAccounts(pending);

    /*
     * Clearing the timer only cancels a search that has not started. A
     * request already in flight still resolves, so it has to know it has
     * been superseded - otherwise the older results overwrite the newer
     * ones and its `finally` clears a spinner belonging to the live search.
     */
    let cancelled = false;

    const searchContacts = async () => {
      if (!pending) {
        setContacts([]);
        return;
      }

      try {
        const results = await searchForContacts(
          selectedSystemId,
          searchQuery.trim()
        );
        if (!cancelled) setContacts(results);
      } catch (error) {
        console.error("Error searching for contacts:", error);
        if (!cancelled) setContacts([]);
      } finally {
        if (!cancelled) setIsSearchingContacts(false);
      }
    };

    const searchAccounts = async () => {
      if (!pending) {
        setAccounts([]);
        return;
      }

      try {
        const results = await searchForAccounts(
          selectedSystemId,
          searchQuery.trim()
        );
        if (!cancelled) setAccounts(results);
      } catch (error) {
        console.error("Error searching for accounts:", error);
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setIsSearchingAccounts(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      /*
       * Started together rather than awaited in turn: they are independent
       * lookups against the same term, and each settles its own state so one
       * erroring cannot take the other's rows down with it.
       */
      searchContacts();
      searchAccounts();
    }, 300); // Debounce search by 300ms

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
  }, [searchQuery, selectedSystemId]);

  // Combine and prioritize results: data projects first, then contacts
  const searchResults = React.useMemo((): SearchResult[] => {
    const results: SearchResult[] = [];
    // Add data projects first
    filteredProjects.forEach((project) => {
      results.push({
        id: project.id,
        name: project.name || "",
        type: "data-project",
      });
    });

    // Add contacts second
    contacts.forEach((contact) => {
      const displayName = `${contact.first_name} ${contact.last_name}${
        contact.company ? ` - ${contact.company}` : ""
      }`;
      results.push({
        id: contact.id,
        name: displayName,
        type: "contact",
        contact,
      });
    });

    /*
     * Accounts last. Rows without a crm_account_id are dropped rather than
     * listed: there is no summary page to open for them, and a row that does
     * nothing when clicked is worse than one that is not offered.
     */
    accounts
      .filter((account) => Boolean(account.crm_account_id))
      .forEach((account) => {
        const displayName = `${account.company}${
          account.website ? ` - ${account.website}` : ""
        }`;
        results.push({
          id: account.id,
          name: displayName,
          type: "account",
          account,
          systemId: selectedSystemId,
        });
      });

    return results;
  }, [filteredProjects, contacts, accounts, selectedSystemId]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    setShowResults(value.trim().length > 0);
    setSelectedIndex(-1); // Reset selection when search changes
  };

  const handleResultSelect = (result: SearchResult) => {
    // Add to recent items
    addRecentItem(result);

    if (result.type === "data-project") {
      navigate(`/data-project/dashboard/${result.id}`);
    } else if (result.type === "contact" && result.contact) {
      setSelectedContactId(result.id);
      setUseCombinedModal(displayCombinedRecordModal());
      setIsContactsModalOpen(true);
    } else if (result.type === "account" && result.account?.crm_account_id) {
      /* Recent items stored before accounts shipped carry no systemId. */
      const systemId = result.systemId ?? selectedSystemId;
      if (displayCombinedRecordModal()) {
        /*
         * Set, not read, on this path: the flag picks between the two
         * contact modals, while the account modal is gated on its own open
         * state. Reading the preference once per selection keeps the two
         * branches honest about which surface the steward asked for.
         */
        setUseCombinedModal(true);
        setSelectedAccount({
          systemId,
          crmAccountId: result.account.crm_account_id,
        });
        setIsAccountModalOpen(true);
      } else {
        navigate(
          `/product/account/${systemId}/${encodeURIComponent(
            result.account.crm_account_id
          )}`
        );
      }
    }
    setSearchQuery("");
    setShowResults(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showResults || searchResults.length === 0) {
      if (event.key === "Escape") {
        setShowResults(false);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex < searchResults.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleResultSelect(searchResults[selectedIndex]);
        } else if (searchResults.length > 0) {
          handleResultSelect(searchResults[0]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setShowResults(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  return (
    <SearchContainer ref={searchRef}>
      <SearchBox>
        <InputWrapper>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <Input
            placeholder={"Search for contacts, accounts, data projects ..."}
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
          />
        </InputWrapper>
        {(recentProjects.length > 0 ||
          recentContacts.length > 0 ||
          recentAccounts.length > 0) && (
          <RecentItemsContainer>
            {recentProjects.map((item) => (
              <Chip
                key={`recent-project-${item.id}`}
                label={item.name}
                onClick={() => handleResultSelect(item)}
                onDelete={() => removeRecentItem(item.id, item.type)}
                size="small"
                color="primary"
                variant="outlined"
                clickable
              />
            ))}
            {recentContacts.map((item) => (
              <Chip
                key={`recent-contact-${item.id}`}
                label={item.name}
                onClick={() => handleResultSelect(item)}
                onDelete={() => removeRecentItem(item.id, item.type)}
                size="small"
                variant="outlined"
                clickable
              />
            ))}
            {recentAccounts.map((item) => (
              <Chip
                key={`recent-account-${item.id}`}
                label={item.name}
                onClick={() => handleResultSelect(item)}
                onDelete={() => removeRecentItem(item.id, item.type)}
                size="small"
                variant="outlined"
                clickable
              />
            ))}
          </RecentItemsContainer>
        )}
      </SearchBox>
      {showResults &&
        (searchResults.length > 0 ||
          isSearchingContacts ||
          isSearchingAccounts) && (
          <SearchResultsContainer>
            <List>
              {searchResults.map((result, index) => (
                <ListItem
                  key={`${result.type}-${result.id}`}
                  disablePadding
                  ref={index === selectedIndex ? selectedItemRef : null}
                  secondaryAction={
                    <Chip
                      label={
                        result.type === "data-project"
                          ? "Data Project"
                          : result.type === "account"
                          ? "Account"
                          : "Contact"
                      }
                      size="small"
                      color={
                        result.type === "data-project" ? "primary" : "default"
                      }
                      /* Outlined so accounts read apart from filled contacts. */
                      variant={
                        result.type === "account" ? "outlined" : "filled"
                      }
                    />
                  }
                >
                  <ListItemButton
                    onClick={() => handleResultSelect(result)}
                    selected={index === selectedIndex}
                  >
                    <ListItemText primary={result.name} />
                  </ListItemButton>
                </ListItem>
              ))}
              {/*
               * Last, because contacts and accounts sort after data projects:
               * this is the place the rows are about to appear in. One row for
               * both searches - which of the two is still running is detail the
               * steward cannot act on.
               */}
              {(isSearchingContacts || isSearchingAccounts) && (
                <ListItem>
                  <CircularProgress size={16} sx={{ mr: 1.5 }} />
                  <Typography variant="body2" color="textSecondary">
                    Searching contacts and accounts ...
                  </Typography>
                </ListItem>
              )}
            </List>
          </SearchResultsContainer>
        )}
      {/*
       * Two review surfaces, one preference, as on the suggested changes
       * grid: the combined record modal is the newer layout, and without the
       * flag this behaves as before. Search has no queue and no grid to
       * update, so a decision is reported to nobody either way.
       */}
      {useCombinedModal ? (
        <RecordReviewModal
          open={isContactsModalOpen}
          onClose={() => setIsContactsModalOpen(false)}
          recordId={selectedContactId}
          batchId={null}
          onSuccess={() => {}}
          integratedSystemId={selectedSystemId}
        />
      ) : (
        <SuggestedChangeDetailsModal
          open={isContactsModalOpen}
          onClose={() => setIsContactsModalOpen(false)}
          recordId={selectedContactId}
          batchId={null}
          onSuccess={(suggestedId) => {}}
          integratedSystemId={selectedSystemId}
        />
      )}
      {/*
       * The account modal sits beside them rather than inside the ternary: an
       * account and a contact can both have been opened in one session, and
       * each surface owns its own open flag.
       */}
      <AccountSummaryModal
        open={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        integratedSystemId={selectedAccount?.systemId}
        crmAccountId={selectedAccount?.crmAccountId}
        onSuccess={() => {}}
      />
    </SearchContainer>
  );
};
